// src/components/OutlookMail/OutlookMailError.tsx

import * as React from "react";
import { OutlookMailResult } from "../../interfaces/outlookMailTypes";
import { outlookColors } from "./outlookMailStyles";

interface OutlookMailErrorProps {
  result: OutlookMailResult;
}

const PERMISSION_ERRORS = ["AUTH_ERROR", "OBO_TOKEN_ERROR", "AUTHENTICATION_ERROR"];
const TIMEOUT_ERRORS = ["CONNECTION_TIMEOUT", "REQUEST_TIMEOUT"];

const OutlookMailError: React.FC<OutlookMailErrorProps> = ({ result }) => {
  const errorCode = result.errorCode || "";
  const isPermissionError = PERMISSION_ERRORS.some((code) => errorCode.includes(code));
  const isTimeoutError = TIMEOUT_ERRORS.some((code) => errorCode.includes(code));

  const getHeader = (): string => {
    if (isPermissionError) return "Access denied";
    if (isTimeoutError) return "Connection timed out";
    if (result.action === "send") return "Failed to send email";
    if (result.action === "draft") return "Failed to create draft";
    return "Email operation failed";
  };

  const getTips = (): string[] => {
    if (isPermissionError) {
      return ["Please sign out and sign back in", "Verify your account has email permissions"];
    }
    if (isTimeoutError) {
      return ["Try again in a moment"];
    }
    return [];
  };

  const bgColor = isTimeoutError ? outlookColors.warningBg : outlookColors.errorBg;
  const textColor = isTimeoutError ? outlookColors.warningColor : outlookColors.errorColor;
  const tips = getTips();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          padding: "10px 12px",
          borderRadius: "6px",
          backgroundColor: bgColor,
          fontSize: "12px",
          lineHeight: "1.5",
          color: textColor,
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          stroke={textColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: "1px" }}
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div>
          <p style={{ margin: 0, fontWeight: 500 }}>{getHeader()}</p>
          {result.errorMessage && (
            <p style={{ margin: "4px 0 0", opacity: 0.8 }}>{result.errorMessage}</p>
          )}
        </div>
      </div>

      {tips.length > 0 && (
        <ul
          style={{
            margin: 0,
            paddingLeft: "20px",
            fontSize: "11px",
            color: outlookColors.mutedForeground,
            lineHeight: "1.6",
          }}
        >
          {tips.map((tip, idx) => (
            <li key={idx}>{tip}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default OutlookMailError;
